// План ↔ Garmin: кнопка «на часы», статусы отправки, предупреждение о несопоставленных
// упражнениях, план против факта для кардио, статус тренированности.

import { useState } from 'react'
import { Link } from 'react-router-dom'

import { Badge, Button, ErrorText, Icon, Modal, Spinner, useToast } from '@/components/ui'
import { formatDate, formatDateTime, formatDuration, formatNumber } from '@/lib/format'

import { planApi, usePlanGarmin, usePlanMutation, useTrainingStatus, type CardioFact, type Planned } from './api'
import { canPushToWatch, garminState, plannedTitle } from './logic'

function UnmappedWarning({ names }: { names: string[] }) {
  if (!names.length) return null
  return (
    <div className="rounded-lg border border-warning/40 bg-warning-soft p-3 text-sm">
      <p className="flex items-start gap-1.5 text-fg">
        <Icon name="alert" size={16} className="mt-0.5 shrink-0 text-warning" />
        <span>Нет в каталоге Garmin — уйдут шагом «другое», название будет в заметке шага:</span>
      </p>
      <ul className="mt-1.5 list-disc space-y-0.5 pl-7 text-fg">
        {names.map((n) => (
          <li key={n}>{n}</li>
        ))}
      </ul>
    </div>
  )
}

/** Подтверждение отправки силовой: что уйдёт на часы и какие упражнения без сопоставления. */
function PushStrengthModal({
  item,
  open,
  onClose,
  onPush,
  pending,
  error,
}: {
  item: Planned
  open: boolean
  onClose: () => void
  onPush: () => void
  pending: boolean
  error: unknown
}) {
  const info = usePlanGarmin(item.id, open)
  const data = info.data
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={item.garmin_workout_id ? 'Отправить на часы заново' : 'Отправить на часы'}
      description={`${plannedTitle(item)} — в календарь Garmin на ${formatDate(item.day)}`}
      footer={
        <Button variant="primary" loading={pending} disabled={!data?.can_push} onClick={onPush}>
          Отправить
        </Button>
      }
    >
      <div className="space-y-3">
        {info.isPending ? <Spinner size={18} /> : null}
        <ErrorText error={info.error} />
        {data ? (
          <>
            <p className="text-sm text-muted">
              Упражнения из целей на этот день — с прогрессией, целевыми повторами и отдыхом.
              {item.garmin_workout_id ? ' Прежняя версия на часах будет заменена.' : ''}
            </p>
            <UnmappedWarning names={data.unmapped} />
            {data.reason ? <ErrorText>{data.reason}</ErrorText> : null}
          </>
        ) : null}
        <ErrorText error={error} />
      </div>
    </Modal>
  )
}

/** Статус отправки и кнопка «на часы» у плановой. */
export function GarminPush({ item }: { item: Planned }) {
  const toast = useToast()
  const [confirm, setConfirm] = useState(false)
  const push = usePlanMutation(() => planApi.pushGarmin(item.id))
  if (!canPushToWatch(item)) return null
  const state = garminState(item)
  const send = () =>
    push.mutate(undefined, {
      onSuccess: () => {
        setConfirm(false)
        toast.show({ message: `На часы: ${plannedTitle(item)}` })
      },
    })
  const start = () => {
    push.reset()
    if (item.kind === 'strength') setConfirm(true)
    else send()
  }
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      {state === 'sent' ? (
        <span className="flex items-center gap-1 text-xs text-muted">
          <Icon name="check" size={14} className="text-accent" />
          Отправлено на часы{item.garmin_pushed_at ? ` · ${formatDateTime(item.garmin_pushed_at)}` : ''}
        </span>
      ) : null}
      {state === 'stale' ? <Badge tone="warning">На часах старая версия</Badge> : null}
      <Button
        size="sm"
        variant={state === 'none' ? 'secondary' : 'ghost'}
        loading={push.isPending && !confirm}
        onClick={start}
      >
        {state === 'none' ? 'На часы' : 'Отправить заново'}
      </Button>
      {!confirm ? <ErrorText error={push.error} className="w-full" /> : null}
      {item.kind === 'strength' ? (
        <PushStrengthModal
          item={item}
          open={confirm}
          onClose={() => setConfirm(false)}
          onPush={send}
          pending={push.isPending}
          error={push.error}
        />
      ) : null}
    </div>
  )
}

function km(m: number | null): string {
  return m === null ? '—' : `${formatNumber(m / 1000, { digits: 1 })} км`
}

function FactTable({ fact }: { fact: CardioFact }) {
  const rows: [string, string, string][] = [
    ['Длительность', formatDuration(fact.planned_duration_s), formatDuration(fact.actual_duration_s)],
  ]
  if (fact.planned_distance_m !== null || fact.actual_distance_m !== null) {
    rows.push(['Дистанция', km(fact.planned_distance_m), km(fact.actual_distance_m)])
  }
  if (fact.hr_zone) {
    rows.push([`Время в зоне ${fact.hr_zone}`, formatDuration(fact.planned_duration_s), formatDuration(fact.zone_time_s)])
  }
  return (
    <div className="mt-2 border-t border-border pt-2">
      <table aria-label="План и факт" className="w-full table-fixed text-sm">
        <thead>
          <tr className="text-left text-xs text-muted">
            <th className="py-1 font-medium" />
            <th className="w-20 py-1 text-right font-medium">План</th>
            <th className="w-20 py-1 text-right font-medium">Факт</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, plan, actual]) => (
            <tr key={label}>
              <td className="truncate py-0.5 text-muted">{label}</td>
              <td className="py-0.5 text-right tabular-nums text-muted">{plan}</td>
              <td className="py-0.5 text-right tabular-nums text-fg">{actual}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <Link
        to={`/activities/${fact.activity_id}`}
        className="mt-1 inline-flex min-h-11 items-center text-sm font-medium text-accent hover:underline lg:min-h-8"
      >
        Активность Garmin: {fact.activity_name ?? 'без названия'}
      </Link>
    </div>
  )
}

/** Выполненное по активности Garmin кардио: план против факта. */
export function CardioFactView({ item }: { item: Planned }) {
  const enabled = item.kind === 'cardio' && item.status === 'done' && item.activity_id !== null
  const info = usePlanGarmin(item.id, enabled)
  if (!enabled) return null
  if (info.isPending) return <Spinner size={16} />
  if (info.error) return <ErrorText error={info.error} className="mt-1" />
  return info.data.fact ? <FactTable fact={info.data.fact} /> : null
}

/** Статус тренированности Garmin (последнее значение `training_status`) для «Нагрузки». */
export function TrainingStatusLine() {
  const status = useTrainingStatus()
  return (
    <div className="mt-2 border-t border-border pt-2">
      <div className="text-xs text-muted">Статус тренированности</div>
      {status.isPending ? (
        <Spinner size={14} />
      ) : status.error ? (
        <ErrorText error={status.error} />
      ) : (
        <div className="text-sm font-medium text-fg">{status.data.label}</div>
      )}
    </div>
  )
}
